/**
 * components/landing/home/ProfessionalTrading.jsx — Section 5.
 *
 * Cinematic trading module with interactive tabs.  Switching a tab
 * updates the preview inside a single MockDashboardChrome frame so the
 * transition feels like navigating a live product.  All previews are
 * DOM-based reproductions of the actual Kalki Ai Cloud trading UI.
 */

import { useState } from 'react'
import clsx from 'clsx'
import { Container, Eyebrow, SectionHeading } from '../design-system'
import { MockDashboardChrome, MiniSparkline, seededWalk } from './_shared'
import { BarChart3, LineChart, ClipboardList, Calculator, Eye, Wallet, Activity, ScanLine, Wand2, Gauge } from 'lucide-react'

const TABS = [
  { id: 'terminal',  icon: BarChart3,     label: 'Terminal' },
  { id: 'charts',    icon: LineChart,     label: 'Charts' },
  { id: 'order',     icon: ClipboardList, label: 'Order Panel' },
  { id: 'risk',      icon: Calculator,    label: 'Risk Calculator' },
  { id: 'watch',     icon: Eye,           label: 'Market Watch' },
  { id: 'portfolio', icon: Wallet,        label: 'Portfolio' },
  { id: 'positions', icon: Activity,      label: 'Open Positions' },
  { id: 'scanner',   icon: ScanLine,      label: 'Scanner' },
  { id: 'strategy',  icon: Wand2,         label: 'Strategy Builder' },
  { id: 'backtest',  icon: Gauge,         label: 'Backtesting' },
]

const SPARK = seededWalk(7, 80, 8, 63000)

const ProfessionalTrading = () => {
  const [active, setActive] = useState('terminal')
  return (
    <section className="py-24 md:py-40">
      <Container>
        <SectionHeading
          eyebrow="Professional Trading"
          title={<>Every trading tool an operator needs, <span className="text-brand-gold">under one product family</span>.</>}
          subtitle="Kalki Ai Cloud ships with a complete institutional-grade trading suite. Switch tabs below to explore each module."
          align="center"
          className="items-center mx-auto"
        />

        {/* Tab bar */}
        <div className="mt-12 -mx-4 px-4 overflow-x-auto scrollbar-hide">
          <div className="inline-flex items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.02] p-1 min-w-max mx-auto">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-medium transition-colors',
                  active === t.id
                    ? 'bg-brand-gold/15 text-brand-gold shadow-[0_0_20px_-5px_rgba(255,193,7,0.5)]'
                    : 'text-zinc-400 hover:text-white hover:bg-white/[0.03]',
                )}
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <MockDashboardChrome className="max-w-6xl mx-auto" innerClass="min-h-[460px]">
            {active === 'terminal'  && <Terminal />}
            {active === 'charts'    && <Charts />}
            {active === 'order'     && <OrderPanel />}
            {active === 'risk'      && <RiskCalc />}
            {active === 'watch'     && <MarketWatch />}
            {active === 'portfolio' && <Portfolio />}
            {active === 'positions' && <Positions />}
            {active === 'scanner'   && <Scanner />}
            {active === 'strategy'  && <StrategyPreview />}
            {active === 'backtest'  && <BacktestPreview />}
          </MockDashboardChrome>
        </div>
      </Container>
    </section>
  )
}

// ─── Tab previews ─────────────────────────────────────────────────────
const Terminal = () => (
  <div className="grid grid-cols-12 gap-2 p-3">
    <div className="col-span-8 rounded-lg bg-white/[0.02] border border-white/5 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] text-zinc-300 font-mono">BTC/USDT <span className="text-brand-emerald ml-2">$63,428.20</span> <span className="text-brand-emerald text-[10px]">+1.82%</span></div>
        <div className="flex items-center gap-1 text-[10px] text-zinc-500">
          {['1m','5m','15m','1H','4H','1D','1W'].map((tf) => (
            <span key={tf} className={clsx('px-1.5 py-0.5 rounded', tf === '4H' ? 'bg-brand-gold/10 text-brand-gold' : '')}>{tf}</span>
          ))}
        </div>
      </div>
      <MiniSparkline data={SPARK} stroke="#FFC107" height={260} width={720} className="w-full" />
    </div>
    <div className="col-span-4 space-y-2">
      <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Order ticket</div>
        <div className="grid grid-cols-2 gap-1 text-[11px]">
          {[['Symbol','BTC-USDT'],['Type','LIMIT'],['Size','0.25'],['Price','63,420']].map(([k,v]) => (
            <div key={k} className="rounded bg-white/[0.03] px-2 py-1.5">
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">{k}</div>
              <div className="text-white">{v}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-1 mt-2">
          <div className="rounded-lg bg-brand-emerald/15 text-brand-emerald text-[11px] text-center py-2 font-semibold">BUY</div>
          <div className="rounded-lg bg-white/[0.03] text-zinc-500 text-[11px] text-center py-2">SELL</div>
        </div>
      </div>
      <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Order book</div>
        <div className="space-y-0.5 font-mono text-[11px]">
          {[63432, 63428, 63424, 63420, 63416].map((p, i) => (
            <div key={p} className="flex justify-between text-red-400/90"><span>{p.toFixed(2)}</span><span className="text-zinc-500">0.{[24,31,55,12,8][i]}2</span></div>
          ))}
          <div className="my-1 border-t border-white/10" />
          {[63412, 63408, 63404, 63400, 63396].map((p, i) => (
            <div key={p} className="flex justify-between text-brand-emerald/90"><span>{p.toFixed(2)}</span><span className="text-zinc-500">0.{[18,42,60,25,15][i]}9</span></div>
          ))}
        </div>
      </div>
    </div>
  </div>
)

const Charts = () => (
  <div className="p-4 grid grid-cols-2 gap-3">
    {[[1,'BTC/USDT','#FFC107'],[2,'ETH/USDT','#22c55e'],[3,'SOL/USDT','#FFC107'],[4,'BNB/USDT','#22c55e']].map(([k,l,c]) => (
      <div key={k} className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] text-zinc-300 font-mono">{l}</div>
          <div className="text-[10px] text-brand-emerald">+2.14%</div>
        </div>
        <MiniSparkline data={seededWalk(k, 60, 6, 100)} stroke={c} height={110} width={340} className="w-full" />
      </div>
    ))}
  </div>
)

const OrderPanel = () => (
  <div className="p-4 grid grid-cols-2 gap-3 min-h-[420px]">
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Recent orders</div>
      <table className="w-full text-[11px]">
        <thead><tr className="text-[9px] uppercase tracking-widest text-zinc-500"><th className="text-left py-1">Time</th><th className="text-left">Symbol</th><th className="text-left">Side</th><th className="text-right">Fill</th></tr></thead>
        <tbody>
          {[['12:04:22','BTC-USDT','BUY',    '0.25 @ 63420', 'brand-emerald'],
            ['12:03:11','ETH-USDT','SELL',   '2.0 @ 3410',   'red-400'],
            ['12:01:48','SOL-USDT','BUY',    '20 @ 154.20',  'brand-emerald'],
            ['12:00:03','BNB-USDT','BUY',    '4 @ 594.80',   'brand-emerald'],
            ['11:58:19','BTC-USDT','SELL',   '0.10 @ 63510', 'red-400']].map(([t,s,side,f,c]) => (
            <tr key={t} className="border-t border-white/5">
              <td className="py-1.5 text-zinc-500 font-mono">{t}</td>
              <td className="text-white">{s}</td>
              <td className={`text-${c}`}>{side}</td>
              <td className="text-right text-zinc-300 font-mono">{f}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Depth of market</div>
      <div className="space-y-0.5 font-mono text-[11px]">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="h-2 rounded bg-red-400/20" style={{ width: `${20 + i * 6}%` }} />
            <div className="text-red-400/90 w-16 text-right">{(63430 + i * 4).toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
)

const RiskCalc = () => (
  <div className="p-4 grid grid-cols-2 gap-3">
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3 space-y-2 text-[12px]">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Position sizing</div>
      {[['Account size','$120,000'],['Risk per trade','1.5%'],['Entry','$63,420'],['Stop-loss','$62,800'],['Distance','$620'],['Position size','2.90 BTC'],['Notional','$183,918'],['R multiple','3.2R']].map(([k,v]) => (
        <div key={k} className="flex items-center justify-between border-b border-white/5 py-1.5">
          <span className="text-zinc-500">{k}</span><span className="text-white font-mono">{v}</span>
        </div>
      ))}
    </div>
    <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Risk breakdown</div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="rounded bg-brand-gold/10 border border-brand-gold/25 p-3"><div className="text-[10px] uppercase text-brand-gold">Max loss</div><div className="text-lg font-semibold text-white mt-1">$1,800</div></div>
        <div className="rounded bg-brand-emerald/10 border border-brand-emerald/25 p-3"><div className="text-[10px] uppercase text-brand-emerald">Target</div><div className="text-lg font-semibold text-white mt-1">$5,760</div></div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-red-500 via-brand-gold to-brand-emerald" style={{ width: '76%' }} /></div>
      <div className="mt-1 text-[10px] text-zinc-500 flex justify-between"><span>Risk 24%</span><span>Reward 76%</span></div>
    </div>
  </div>
)

const MarketWatch = () => (
  <div className="p-4">
    <table className="w-full text-[12px]">
      <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500"><th className="text-left py-2">Symbol</th><th className="text-right">Last</th><th className="text-right">24h</th><th className="text-right">Volume</th><th className="text-right">Chart</th></tr></thead>
      <tbody>
        {[['BTC/USDT','63,428.20','+1.82%','$28.4B'],['ETH/USDT','3,412.50','-0.42%','$14.2B'],['SOL/USDT','154.20','+3.10%','$2.8B'],['BNB/USDT','594.80','+0.72%','$1.9B'],['XRP/USDT','0.5240','-1.10%','$0.9B'],['ADA/USDT','0.4380','+0.20%','$0.6B'],['DOGE/USDT','0.1620','+2.60%','$0.8B']].map(([s,l,ch,v], i) => (
          <tr key={s} className="border-t border-white/5">
            <td className="py-2 text-white font-mono">{s}</td>
            <td className="text-right text-white font-mono">{l}</td>
            <td className={clsx('text-right font-mono', ch.startsWith('-') ? 'text-red-400' : 'text-brand-emerald')}>{ch}</td>
            <td className="text-right text-zinc-400 font-mono">{v}</td>
            <td className="text-right"><MiniSparkline data={seededWalk(i+1, 40, 6, 100)} stroke={ch.startsWith('-') ? '#ef4444' : '#10B981'} height={20} width={100} className="inline-block" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const Portfolio = () => (
  <div className="p-4 grid grid-cols-3 gap-3">
    <div className="col-span-2 rounded-lg bg-white/[0.02] border border-white/5 p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Equity</div>
      <MiniSparkline data={seededWalk(5, 80, 4, 120)} stroke="#10B981" height={180} width={520} className="w-full" />
    </div>
    <div className="space-y-2">
      {[['NAV','$120,432','+3.4%'],['Cash','$18,220','—'],['Positions','7 open','—'],['Realised','+$4,120','+8.1%']].map(([k,v,d], i) => (
        <div key={k} className="rounded bg-white/[0.02] border border-white/5 px-3 py-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k}</div>
          <div className="text-white text-sm mt-0.5">{v}</div>
          <div className={clsx('text-[10px] font-mono', (i === 0 || i === 3) ? 'text-brand-emerald' : 'text-zinc-500')}>{d}</div>
        </div>
      ))}
    </div>
  </div>
)

const Positions = () => (
  <div className="p-4">
    <table className="w-full text-[12px]">
      <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500"><th className="text-left py-2">Symbol</th><th className="text-right">Size</th><th className="text-right">Entry</th><th className="text-right">Mark</th><th className="text-right">P&L</th><th className="text-right">R</th></tr></thead>
      <tbody>
        {[['BTC-USDT','+0.50','62,180','63,428','+$624','2.0R'],['ETH-USDT','+4.0','3,320','3,412','+$368','1.4R'],['SOL-USDT','+40','148.20','154.20','+$240','1.2R'],['BNB-USDT','-2.0','598.20','594.80','+$6.8','0.3R'],['XRP-USDT','+2000','0.5310','0.5240','-$14','-0.5R']].map(([s,size,e,m,p,r], i) => (
          <tr key={s} className="border-t border-white/5">
            <td className="py-2 text-white font-mono">{s}</td>
            <td className={clsx('text-right font-mono', size.startsWith('-') ? 'text-red-400' : 'text-brand-emerald')}>{size}</td>
            <td className="text-right text-zinc-400 font-mono">{e}</td>
            <td className="text-right text-zinc-300 font-mono">{m}</td>
            <td className={clsx('text-right font-mono', p.startsWith('-') ? 'text-red-400' : 'text-brand-emerald')}>{p}</td>
            <td className="text-right text-zinc-300 font-mono">{r}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const Scanner = () => (
  <div className="p-4">
    <div className="flex items-center gap-2 mb-3">
      {['Breakout','Momentum','Compression','Divergence','Volume spike'].map((f, i) => (
        <span key={f} className={clsx('rounded-full border px-2.5 py-1 text-[11px]', i === 0 ? 'border-brand-gold/40 bg-brand-gold/10 text-brand-gold' : 'border-white/10 text-zinc-400')}>{f}</span>
      ))}
    </div>
    <div className="grid grid-cols-4 gap-2">
      {['BTC/USDT','ETH/USDT','SOL/USDT','BNB/USDT','LINK/USDT','AVAX/USDT','ARB/USDT','OP/USDT'].map((s, i) => (
        <div key={s} className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-mono text-white">{s}</div>
            <div className="text-[10px] text-brand-emerald">+{(1 + i * 0.6).toFixed(2)}%</div>
          </div>
          <MiniSparkline data={seededWalk(i+11, 30, 6, 100)} stroke="#FFC107" height={40} width={180} className="mt-1 w-full" />
          <div className="text-[10px] text-zinc-500 mt-1">Break of structure · 4H</div>
        </div>
      ))}
    </div>
  </div>
)

const StrategyPreview = () => (
  <div className="p-6 min-h-[420px] flex flex-col justify-center">
    <div className="flex items-center justify-between mb-6">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Strategy · SMC Trend Continuation</div>
      <div className="text-[10px] text-brand-emerald font-mono">Deployed · v3</div>
    </div>
    <div className="grid grid-cols-6 gap-2">
      {['Trigger','Filter','Confirmation','Entry','Manage','Exit'].map((s, i) => (
        <div key={s} className="rounded-xl border border-white/5 bg-white/[0.02] p-3 relative">
          <div className="h-8 w-8 rounded-lg bg-brand-gold/10 text-brand-gold flex items-center justify-center text-[11px] font-semibold mb-2">{i+1}</div>
          <div className="text-[12px] font-semibold text-white">{s}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{['BOS on 4H','Trend up on 1D','OB retest + vol','Limit @ OB','Trail stop','1st TP + BE'][i]}</div>
        </div>
      ))}
    </div>
    <MiniSparkline data={seededWalk(9, 80, 5, 100)} stroke="#10B981" height={140} width={860} className="mt-8 w-full" />
  </div>
)

const BacktestPreview = () => (
  <div className="p-4 grid grid-cols-3 gap-3">
    <div className="col-span-2 rounded-lg bg-white/[0.02] border border-white/5 p-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Equity curve</div>
      <MiniSparkline data={seededWalk(11, 100, 5, 100)} stroke="#10B981" height={260} width={620} className="w-full" />
    </div>
    <div className="space-y-2">
      {[['Trades','142'],['Win-rate','61.4%'],['Profit factor','1.83'],['Max DD','-6.4%'],['Sharpe','1.42'],['Sortino','1.98'],['Avg R','1.32R'],['Expectancy','+0.42R']].map(([k,v]) => (
        <div key={k} className="flex items-center justify-between rounded bg-white/[0.02] border border-white/5 px-2.5 py-1.5 text-[11px]">
          <span className="text-zinc-500">{k}</span><span className="text-white font-mono">{v}</span>
        </div>
      ))}
    </div>
  </div>
)

export default ProfessionalTrading
